import { useCallback } from 'react'

import { useMutation, useQueryClient } from 'react-query'

import { useRestrictedWordsSettings } from '@/modules/restricted-words/hooks/use-restricted-words-settings.ts'
import { restrictedWordsService } from '@/modules/restricted-words/service/restricted-words-service.ts'
import type { UpdateRestrictedWordsPayload } from '@/modules/restricted-words/types.ts'
import { handleError } from '@/utils/error'

export function useUpdateRestrictedWordsSettings() {
  const queryClient = useQueryClient()
  const { settings } = useRestrictedWordsSettings()

  const updateMutation = useMutation(
    (settings: Partial<UpdateRestrictedWordsPayload>) =>
      restrictedWordsService.updateRestrictedWordsSettings(settings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['restrictedWordsSettings'])
      },
      onError: (error) =>
        handleError(error, 'Failed to update restricted words settings'),
    },
  )

  const updateRestrictedWordsSettings = useCallback(
    (field: keyof UpdateRestrictedWordsPayload, state: boolean) => {
      return updateMutation.mutateAsync({ ...settings, [field]: state })
    },
    [settings, updateMutation],
  )

  return {
    updateRestrictedWordsSettings,
    isUpdateSettingsLoading: updateMutation.isLoading,
  }
}
